import React from 'react';
import styles from './Badge.module.scss';

export type BadgeSize = 'sm' | 'md';

export type BadgeProps = {
  /** The number or count to display in the badge. If greater than 99, displays "99+" */
  count: number;
  /** Size variant of the badge */
  size?: BadgeSize;
  /** Maximum count to display (default: 99). Larger values show "max+" */
  max?: number;
  /** Additional CSS class names */
  className?: string;
  /** Required test anchor for Playwright and unit tests */
  'data-testid': string;
};

/**
 * Badge component displays a notification count indicator.
 * Used for showing unread notifications, messages, or alerts.
 */
export const Badge: React.FC<BadgeProps> = ({
  count,
  size = 'md',
  max = 99,
  className,
  'data-testid': testId,
}) => {
  const displayCount = count > max ? `${max}+` : count.toString();

  const classNames = [styles.badge, styles[`badge--${size}`], className]
    .filter(Boolean)
    .join(' ');

  if (count <= 0) {
    return null;
  }

  return (
    <span
      className={classNames}
      data-testid={testId}
      aria-label={`${count} ${count === 1 ? 'notification' : 'notifications'}`}
      role="status"
    >
      {displayCount}
    </span>
  );
};

Badge.displayName = 'Badge';

export default Badge;
